import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import Logo from "../components/Logo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";

export default function AcceptInvite() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, captureToken } = useAuth();
  // Initialize from the URL ?token=... once; user can override via the input.
  const [token, setToken] = useState(() => search.get("token") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [gdpr, setGdpr] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // The token can either be typed manually or arrive via ?token=... in the URL.
  // The lazy useState initializer above handles the initial value; nothing else
  // is needed here.

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) return setErr("Passordet må være minst 6 tegn");
    if (password !== confirm) return setErr("Passordene må være like");
    if (!gdpr) return setErr("Du må godta personvernerklæringen for å fortsette.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", {
        token,
        password,
        gdpr_accepted: true,
      });
      const u = captureToken(data);
      setUser(u);
      if (u.role === "kunde") navigate("/portal");
      else if (u.role === "admin") navigate("/admin");
      else if (u.role === "service") navigate("/service");
      else navigate("/vask");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] px-6 py-12">
      <div className="w-full max-w-md">
        <Logo />
        <h1 className="mt-10 text-2xl sm:text-3xl font-bold text-[#0C1824]">Aktiver konto</h1>
        <p className="mt-1 text-sm text-[#2B3F4D]">
          Sett et passord for å fullføre invitasjonen.
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="invite-form">
          <div className="space-y-1.5">
            <Label htmlFor="token">Invitasjonskode</Label>
            <Input
              id="token"
              data-testid="invite-token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Nytt passord</Label>
            <Input
              id="password"
              data-testid="invite-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm">Gjenta passord</Label>
            <Input
              id="confirm"
              data-testid="invite-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          <div className="flex items-start gap-2 pt-2">
            <Checkbox
              id="gdpr"
              checked={gdpr}
              onCheckedChange={(v) => setGdpr(!!v)}
              data-testid="invite-gdpr"
              className="mt-0.5"
            />
            <Label
              htmlFor="gdpr"
              className="text-sm font-normal text-[#2B3F4D] leading-relaxed cursor-pointer"
            >
              Jeg har lest og godtar{" "}
              <Link to="/personvern" target="_blank" className="text-[#A57F39] underline">
                personvernerklæringen
              </Link>
              .
            </Label>
          </div>
          {err && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
          <Button
            type="submit"
            data-testid="invite-submit"
            disabled={loading}
            className="w-full bg-[#BD984D] hover:bg-[#A57F39] text-white h-11"
          >
            {loading ? "Aktiverer…" : "Aktiver konto"}
          </Button>
        </form>
      </div>
    </div>
  );
}
